import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { emotionImagesData } from './data/stage1EmotionQuestions';
import './App.css';

const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

function EmotionCornerStage2() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionInfo = location.state || {};  // { sessionId, childName, ... }

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:7000';

  // ── Photo mode: 'default' shows built-in/gallery photos; 'upload' is manual ─
  const [photoMode, setPhotoMode] = useState('default');

  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [hintMessage, setHintMessage] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isLoadingHint, setIsLoadingHint] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [detectedEmotion, setDetectedEmotion] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // ── Camera state ──────────────────────────────────────────────────────────
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');

  // ── Answer tracking ───────────────────────────────────────────────────────
  const [answers, setAnswers] = useState([]);          // accumulated attempts
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [savedSessionId, setSavedSessionId] = useState(null);
  const [imageUploadTime, setImageUploadTime] = useState(null);  // when photo was loaded
  const [stageStartTime] = useState(() => Date.now());

  // ── Default photo mode state ──────────────────────────────────────────────
  const [galleryImages, setGalleryImages] = useState([]);
  const [defaultPhotoIndex, setDefaultPhotoIndex] = useState(0);
  const [defaultAnswered, setDefaultAnswered] = useState(false);
  const [defaultSelected, setDefaultSelected] = useState(null);
  const [defaultPhotoStartTime, setDefaultPhotoStartTime] = useState(() => Date.now());

  // ── Pre-warm speech synthesis voices on mount ────────────────────────────
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
    }
  }, []);

  // ── Fetch gallery images from backend ─────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${apiUrl}/api/gallery`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) {
          setGalleryImages(data.map(img => ({
            id: img.id,
            emotion: img.emotion,
            imageSrc: img.image_data,
          })));
        }
      })
      .catch(() => {});
  }, [apiUrl]);

  // Merged + shuffled pool of default photos (gallery + built-ins)
  const defaultPhotoPool = useMemo(() => {
    const merged = [
      ...emotionImagesData.filter(img => img.imageSrc).map(img => ({ id: img.id, emotion: img.emotion, imageSrc: img.imageSrc })),
      ...galleryImages,
    ];
    return shuffleArray(merged);
  }, [galleryImages]);

  const currentDefaultPhoto = defaultPhotoPool[defaultPhotoIndex % Math.max(defaultPhotoPool.length, 1)];

  // ── Camera cleanup on unmount ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraStream]);

  const openCamera = async () => {
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      setShowCamera(true);
      // attach stream to video element after modal renders
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError('Camera permission denied. Please allow camera access and try again.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraError('No camera found on this device.');
      } else {
        setCameraError('Could not access camera: ' + err.message);
      }
    }
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
    setCameraError('');
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg');
    setUploadedImage(dataUrl);
    setDetectedEmotion(null);
    setSelectedEmotion(null);
    setFeedback(null);
    setShowHint(false);
    setImageUploadTime(Date.now());
    closeCamera();
  };

  // ── Pick the best calm female voice available ─────────────────────────────
  const getFemaleVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    const preferred = [
      'Google UK English Female',
      'Microsoft Zira - English (United States)',
      'Microsoft Libby Online (Natural) - English (United Kingdom)',
      'Microsoft Aria Online (Natural) - English (United States)',
      'Samantha',
      'Karen',
      'Moira',
    ];
    for (const name of preferred) {
      const v = voices.find(v => v.name === name);
      if (v) return v;
    }
    return (
      voices.find(v => /female/i.test(v.name) && /en[-_]/i.test(v.lang)) ||
      voices.find(v => /female/i.test(v.name)) ||
      voices.find(v => /en[-_]/i.test(v.lang)) ||
      null
    );
  };

  const speakText = (text) => {
    if (!soundEnabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const speak = () => {
      const utterance = new SpeechSynthesisUtterance(text);
      const voice = getFemaleVoice();
      if (voice) utterance.voice = voice;
      utterance.lang   = 'en-US';
      utterance.rate   = 0.5;
      utterance.pitch  = 1.05;
      utterance.volume = 1;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend   = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    };
    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.onvoiceschanged = null;
        speak();
      };
    } else {
      speak();
    }
  };

  // Play celebratory bell/chime sound using Web Audio API
  const playClap = () => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const now = audioContext.currentTime;

      // Bright bell/chime celebration sounds
      const chimes = [
        { freq: 1046.50, duration: 0.4, delay: 0 },      // C6 - bright ding
        { freq: 1318.51, duration: 0.35, delay: 0.15 },  // E6 - higher ring
        { freq: 1567.98, duration: 0.5, delay: 0.3 },    // G6 - highest chime
        { freq: 1046.50, duration: 0.4, delay: 0.5 },    // C6 again
        { freq: 1567.98, duration: 0.6, delay: 0.65 },   // G6 final note
      ];

      chimes.forEach(chime => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        const filter = audioContext.createBiquadFilter();

        // Use sine wave for clean, pure bell tone
        osc.type = 'sine';
        osc.frequency.setValueAtTime(chime.freq, now + chime.delay);

        // Add slight frequency modulation for bell-like character
        osc.frequency.setTargetAtTime(chime.freq * 0.95, now + chime.delay, 0.08);

        // Setup low-pass filter for warm bell tone
        filter.type = 'lowpass';
        filter.frequency.value = 4000;

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(audioContext.destination);

        // Bell envelope - quick attack, long decay
        gain.gain.setValueAtTime(0, now + chime.delay);
        gain.gain.linearRampToValueAtTime(0.7, now + chime.delay + 0.03);  // Quick attack
        gain.gain.exponentialRampToValueAtTime(0.05, now + chime.delay + chime.duration);  // Long decay
        gain.gain.linearRampToValueAtTime(0, now + chime.delay + chime.duration + 0.1);

        osc.start(now + chime.delay);
        osc.stop(now + chime.delay + chime.duration + 0.1);
      });

    } catch (err) {
      console.log('Celebration sound could not be played:', err);
    }
  };

  const emotions = [
    { name: 'Happy',     emoji: '😊', bg: '#FFF9DB', border: '#FFE066', text: '#B7791F' },
    { name: 'Sad',       emoji: '😢', bg: '#EBF8FF', border: '#90CDF4', text: '#2B6CB0' },
    { name: 'Angry',     emoji: '😠', bg: '#FFF5F5', border: '#FEB2B2', text: '#C53030' },
    { name: 'Surprised', emoji: '😮', bg: '#F0FFF4', border: '#9AE6B4', text: '#276749' },
    { name: 'Scared',    emoji: '😨', bg: '#FAF5FF', border: '#D6BCFA', text: '#6B46C1' },
    { name: 'Neutral',   emoji: '😐', bg: '#FAFAFA', border: '#E2E8F0', text: '#4A5568' },
  ];

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadedImage(ev.target.result);
      setDetectedEmotion(null);
      setSelectedEmotion(null);
      setFeedback(null);
      setShowHint(false);
      setImageUploadTime(Date.now());
    };
    reader.readAsDataURL(file);
  };

  const handleEmotionSelect = async (emotion) => {
    if (isAnalyzing) return;
    setSelectedEmotion(emotion);
    setFeedback(null);

    if (!uploadedImage) {
      setFeedback({ type: 'info', msg: 'Upload a photo first, then pick an emotion! 📸' });
      return;
    }

    setIsAnalyzing(true);
    setFeedback({ type: 'info', msg: 'Analyzing the face... 🔍' });

    try {
      const res = await fetch(`${apiUrl}/api/analyze-emotion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: uploadedImage.split(',')[1] }),
      });

      if (res.ok) {
        const data = await res.json();
        const apiEmotion = data.detected_emotion;
        setDetectedEmotion(apiEmotion);
        const isCorrect = emotion.name === apiEmotion;

        // Record this attempt with time taken since image was uploaded
        const timeTakenMs = imageUploadTime ? Date.now() - imageUploadTime : null;
        setAnswers(prev => [...prev, {
          question_index: prev.length,
          correct_emotion: apiEmotion,
          selected_emotion: emotion.name,
          is_correct: isCorrect,
          time_taken_ms: timeTakenMs,
        }]);

        if (isCorrect) {
          playClap();
          setShowCelebration(true);
          setFeedback({ type: 'success', msg: `Amazing! That's correct — the face shows ${apiEmotion}! 🎉` });
          setTimeout(() => setShowCelebration(false), 4000);
        } else {
          setFeedback({ type: 'error', msg: `Not quite! The face shows ${apiEmotion}. Try again! 🤔` });
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail || '';
        const noFace = detail.toLowerCase().includes('no face') || detail.toLowerCase().includes('no valid');
        setFeedback({
          type: 'error',
          msg: noFace
            ? 'No face detected in the image. Please upload a clear photo of a face. 📸'
            : 'Could not analyze the image. Try a different photo! 🤔',
        });
      }
    } catch {
      setFeedback({ type: 'error', msg: 'Server error. Make sure the backend is running!' });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGetHelp = async () => {
    if (!uploadedImage) {
      setFeedback({ type: 'info', msg: 'Upload a photo first! 📸' });
      return;
    }

    let emotionToUse = detectedEmotion;

    // If emotion hasn't been detected yet, detect it first
    if (!detectedEmotion) {
      try {
        setFeedback({ type: 'info', msg: 'Analyzing the face... 🔍' });
        const res = await fetch(`${apiUrl}/api/analyze-emotion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_base64: uploadedImage.split(',')[1] }),
        });

        if (res.ok) {
          const data = await res.json();
          emotionToUse = data.detected_emotion;
          setDetectedEmotion(emotionToUse);
        } else {
          const errData = await res.json().catch(() => ({}));
          const detail = errData.detail || '';
          const noFace = detail.toLowerCase().includes('no face') || detail.toLowerCase().includes('no valid');
          setFeedback({
            type: 'error',
            msg: noFace
              ? 'No face detected in the image. Please upload a clear photo of a face. 📸'
              : 'Could not analyze the image. Try a different photo! 🤔',
          });
          return;
        }
      } catch {
        console.error('Failed to detect emotion');
        setFeedback({ type: 'error', msg: 'Server error. Make sure the backend is running!' });
        return;
      }
    }

    // Check if emotion is Disgust — if so, ask to try another image
    if (emotionToUse === 'Disgust') {
      setFeedback({ type: 'info', msg: 'Try uploading an image with a different emotion in the options! 📸' });
      setSelectedEmotion(null);
      return;
    }

    setShowHint(true);
    setIsLoadingHint(true);
    setHintMessage('Loading hint...');

    try {
      const res = await fetch(`${apiUrl}/api/get-hint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emotion: emotionToUse || '',
          image_base64: uploadedImage ? uploadedImage.split(',')[1] : null,
        }),
      });
      if (!res.ok) {
        // Handle error but continue with hint display
      }
      // Display only the emotion name in hint box
      setHintMessage(emotionToUse);
      // Speak the emotion name three times
      const emotionSpeak = `${emotionToUse} ${emotionToUse} ${emotionToUse}`;
      speakText(emotionSpeak);
    } catch {
      // Display only the emotion name in hint box
      setHintMessage(emotionToUse);
      // Speak the emotion name three times
      const emotionSpeak = `${emotionToUse} ${emotionToUse} ${emotionToUse}`;
      speakText(emotionSpeak);
    } finally {
      setIsLoadingHint(false);
      // After 5 seconds, hide hint and reset for the user to try again
      setTimeout(() => {
        setShowHint(false);
        setSelectedEmotion(null);
        setFeedback(null);
      }, 5000);
    }
  };

  // ── Default photo mode answer handler ────────────────────────────────────
  const handleDefaultAnswer = (emotion) => {
    if (defaultAnswered || !currentDefaultPhoto) return;
    const isCorrect = emotion.name === currentDefaultPhoto.emotion;
    const timeTakenMs = Date.now() - defaultPhotoStartTime;
    setDefaultSelected(emotion.name);
    setDefaultAnswered(true);
    if (isCorrect) {
      playClap();
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
    }
    setAnswers(prev => [...prev, {
      question_index: prev.length,
      correct_emotion: currentDefaultPhoto.emotion,
      selected_emotion: emotion.name,
      is_correct: isCorrect,
      time_taken_ms: timeTakenMs,
    }]);
  };

  const handleNextDefaultPhoto = () => {
    setDefaultPhotoIndex(i => i + 1);
    setDefaultAnswered(false);
    setDefaultSelected(null);
    setDefaultPhotoStartTime(Date.now());
    setShowCelebration(false);
  };

  // ── Submit all attempts to backend ──────────────────────────────────────
  const submitResults = async () => {
    if (!sessionInfo.sessionId) {
      setSubmitError('No session linked. Please start from the Emotion Corner form.');
      return;
    }
    if (answers.length === 0) {
      setSubmitError('No attempts recorded yet. Upload a photo and guess an emotion first.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const token = localStorage.getItem('authToken');
      const correctCount = answers.filter(a => a.is_correct).length;
      const res = await fetch(`${apiUrl}/api/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionInfo.sessionId,
          stage: 'training',
          score: correctCount,
          total: answers.length,
          answers,
          total_time_ms: Date.now() - stageStartTime,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save results');
      setSavedSessionId(sessionInfo.sessionId);
      setSubmitted(true);
      setFeedback({ type: 'success', msg: `✅ Session saved! ${correctCount}/${answers.length} correct.` });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-shell">
      {/* ── Header ── */}
      <header className="app-header">
        <div className="header-brand">
          <button className="back-home-btn" onClick={() => navigate('/emotion-corner', { state: sessionInfo })} title="Back to Emotion Corner">
            ← Back
          </button>
          <div className="brand-icon">😊</div>
          <span className="brand-title">Training - Emotion Corner</span>
        </div>
        <button
          onClick={() => {
            if (soundEnabled && window.speechSynthesis) window.speechSynthesis.cancel();
            setSoundEnabled(prev => !prev);
          }}
          title={soundEnabled ? 'Sound On — click to mute' : 'Sound Off — click to enable'}
          style={{
            background: soundEnabled ? '#DCFCE7' : '#F3F4F6',
            border: `2px solid ${soundEnabled ? '#86EFAC' : '#D1D5DB'}`,
            borderRadius: '10px',
            padding: '6px 14px',
            fontSize: '18px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: '700',
            color: soundEnabled ? '#166534' : '#6B7280',
          }}
        >
          {soundEnabled ? '🔊' : '🔇'}
          <span style={{ fontSize: '13px' }}>{soundEnabled ? 'Sound On' : 'Sound Off'}</span>
        </button>
      </header>

      {/* ── Progress ── */}
      <div className="progress-track">
        <div className="progress-fill"></div>
      </div>

      {/* ── Main ── */}
      <main className="app-main">
        {/* ─── LEFT COLUMN ─── */}
        <div className="left-col">
          {/* Mode tab switcher */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', width: '100%' }}>
            {[{ key: 'default', label: '🖼️ Default Photo' }, { key: 'upload', label: '📤 Upload / Camera' }].map(tab => (
              <button
                key={tab.key}
                onClick={() => { setPhotoMode(tab.key); setFeedback(null); setSelectedEmotion(null); }}
                style={{
                  flex: 1, padding: '8px 4px', fontSize: '13px', fontWeight: '700',
                  border: '2px solid', borderRadius: '10px', cursor: 'pointer',
                  background: photoMode === tab.key ? '#2563EB' : '#F3F4F6',
                  color: photoMode === tab.key ? '#fff' : '#4B5563',
                  borderColor: photoMode === tab.key ? '#2563EB' : '#D1D5DB',
                  transition: 'all 0.15s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {photoMode === 'default' ? (
            <>
              <div className="image-card">
                {defaultPhotoPool.length > 0 && currentDefaultPhoto ? (
                  <img src={currentDefaultPhoto.imageSrc} alt="Emotion face" className="emotion-image" />
                ) : (
                  <div className="image-placeholder">
                    <span className="placeholder-icon">🖼️</span>
                    <span className="placeholder-text">No photos available</span>
                  </div>
                )}
              </div>
              {defaultAnswered && (
                <button
                  onClick={handleNextDefaultPhoto}
                  style={{ marginTop: '10px', width: '100%', padding: '10px', background: 'linear-gradient(135deg,#2563EB,#1D4ED8)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '800', cursor: 'pointer' }}
                >
                  ▶ Next Photo
                </button>
              )}
            </>
          ) : (
            <>
              {/* Image card */}
              <div className="image-card">
                {uploadedImage ? (
                  <img src={uploadedImage} alt="Emotion face" className="emotion-image" />
                ) : (
                  <div className="image-placeholder">
                    <span className="placeholder-icon">🖼️</span>
                    <span className="placeholder-text">Upload a photo to begin!</span>
                  </div>
                )}
              </div>

              {/* Upload card */}
              <label htmlFor="mainUpload" className="upload-card">
                <input id="mainUpload" type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                <span className="upload-icon">📤</span>
                <span className="upload-text">Upload photo!</span>
              </label>

              {/* Camera card */}
              <button className="upload-card" onClick={openCamera} style={{ cursor: 'pointer', border: 'none', background: undefined }}>
                <span className="upload-icon">📷</span>
                <span className="upload-text">Use Camera</span>
              </button>

              {/* Camera error message */}
              {cameraError && (
                <div style={{ marginTop: '8px', background: '#FFF5F5', border: '1.5px solid #FCA5A5', borderRadius: '10px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', color: '#991B1B' }}>
                  ⚠️ {cameraError}
                </div>
              )}
            </>
          )}
        </div>

        {/* ─── RIGHT COLUMN ─── */}
        <div className="right-col">
          {/* Limitation notice */}
          <div style={{ background: '#EFF6FF', border: '1.5px solid #BFDBFE', color: '#1E40AF', borderRadius: '10px', padding: '8px 12px', fontSize: '13px', marginBottom: '12px' }}>
            ℹ️ Note: This tool works best with a single, clear frontal face photo.
          </div>

          <div className="question-block">
            <h2 className="question-title">How do they feel?</h2>
            <p className="question-sub">Tap the emoji that matches the face.</p>
          </div>

          {/* Emotion grid */}
          <div className="emotion-grid">
            {emotions.map((em) => {
              // Default photo mode: compare against known emotion label
              const isDefaultCorrect = photoMode === 'default' && defaultAnswered && em.name === currentDefaultPhoto?.emotion;
              const isDefaultWrong = photoMode === 'default' && defaultAnswered && defaultSelected === em.name && !isDefaultCorrect;

              // Upload mode: compare against AI-detected emotion
              const isSelected = selectedEmotion?.name === em.name;
              const isUploadCorrect = photoMode === 'upload' && showHint && detectedEmotion && em.name === detectedEmotion;

              const isCorrect = isDefaultCorrect || isUploadCorrect;
              const isWrong = isDefaultWrong;
              const isDisabled = photoMode === 'default' ? defaultAnswered : isAnalyzing;

              return (
                <button
                  key={em.name}
                  onClick={() => photoMode === 'default' ? handleDefaultAnswer(em) : handleEmotionSelect(em)}
                  className={`emotion-btn ${isCorrect ? 'detected-correct' : ''}`}
                  disabled={isDisabled}
                  style={{
                    background: isCorrect ? '#10b981' : isWrong ? '#ef4444' : em.bg,
                    borderColor: isSelected ? em.border : 'transparent',
                    boxShadow: isCorrect
                      ? '0 0 0 3px #16A34A, 0 0 20px rgba(34,197,94,0.8)'
                      : isSelected ? `0 0 0 3px ${em.border}` : '0 2px 8px rgba(0,0,0,0.06)',
                    opacity: isDisabled && !isCorrect && !isWrong ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    color: isCorrect || isWrong ? '#fff' : em.text,
                    border: '2px solid',
                  }}
                >
                  <span className="em-emoji">{em.emoji}</span>
                  <span className="em-name" style={{ color: isCorrect || isWrong ? '#fff' : em.text }}>{em.name}</span>
                </button>
              );
            })}
          </div>

          {/* Feedback for default mode */}
          {photoMode === 'default' && defaultAnswered && currentDefaultPhoto && (
            <div className={`feedback-bar ${defaultSelected === currentDefaultPhoto.emotion ? 'success' : 'error'}`}>
              {defaultSelected === currentDefaultPhoto.emotion
                ? `✓ Correct! The emotion is ${currentDefaultPhoto.emotion}! 🎉`
                : `✗ The correct emotion is ${currentDefaultPhoto.emotion}. Keep trying! 💪`}
            </div>
          )}

          {/* Feedback for upload mode */}
          {photoMode === 'upload' && feedback && (
            <div className={`feedback-bar ${feedback.type}`}>
              {feedback.msg}
            </div>
          )}

          {/* Get Help — upload mode only */}
          {photoMode === 'upload' && (
            <button className="help-btn" onClick={handleGetHelp}>
              💡 Get Help
            </button>
          )}

          {/* ── Attempt counter + Finish button ── */}
          {answers.length > 0 && !submitted && photoMode === 'upload' && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#4B5563', textAlign: 'center' }}>
                📸 {answers.length} attempt{answers.length !== 1 ? 's' : ''} recorded &nbsp;·&nbsp;
                ✅ {answers.filter(a => a.is_correct).length} correct
              </div>
              <button
                onClick={submitResults}
                disabled={isSubmitting}
                style={{
                  padding: '11px 0', width: '100%', background: isSubmitting ? '#86EFAC' : '#059669',
                  color: '#fff', border: 'none', borderRadius: '14px', fontSize: '15px',
                  fontWeight: '800', cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Segoe UI, Inter, sans-serif',
                }}
              >
                {isSubmitting ? '⏳ Saving...' : '💾 Finish & Save Session'}
              </button>
            </div>
          )}

          {/* ── Shared attempt counter + Finish button (default mode) ── */}
          {answers.length > 0 && !submitted && photoMode === 'default' && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#4B5563', textAlign: 'center' }}>
                🖼️ {answers.length} attempt{answers.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
                ✅ {answers.filter(a => a.is_correct).length} correct
              </div>
              <button
                onClick={submitResults}
                disabled={isSubmitting}
                style={{
                  padding: '11px 0', width: '100%', background: isSubmitting ? '#86EFAC' : '#059669',
                  color: '#fff', border: 'none', borderRadius: '14px', fontSize: '15px',
                  fontWeight: '800', cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Segoe UI, Inter, sans-serif',
                }}
              >
                {isSubmitting ? '⏳ Saving...' : '💾 Finish & Save Session'}
              </button>
            </div>
          )}

          {/* Submitted state */}
          {submitted && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ background: '#F0FDF4', border: '1.5px solid #86EFAC', borderRadius: '12px', padding: '10px 14px', fontSize: '14px', fontWeight: '700', color: '#166534', textAlign: 'center' }}>
                ✅ Results saved to database!
              </div>
              {savedSessionId && (
                <button
                  onClick={() => navigate(`/report/${savedSessionId}`, { state: sessionInfo })}
                  style={{ padding: '11px 0', width: '100%', background: '#1E40AF', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '15px', fontWeight: '800', cursor: 'pointer', fontFamily: 'Segoe UI, Inter, sans-serif' }}
                >
                  📊 View Report
                </button>
              )}
            </div>
          )}

          {/* Submit error */}
          {submitError && (
            <div style={{ marginTop: '8px', background: '#FFF5F5', border: '1.5px solid #FCA5A5', borderRadius: '12px', padding: '10px 14px', fontSize: '13px', fontWeight: '600', color: '#991B1B' }}>
              ⚠️ {submitError}
            </div>
          )}

          {/* Hint — shown below the Get Help button */}
          {showHint && (
            <div className="hint-card">
              <div className="hint-header">
                <span className="hint-label">💡 HINT</span>
                <button
                  className={`hint-audio-btn ${isSpeaking ? 'speaking' : ''}`}
                  onClick={() => !isLoadingHint && speakText(hintMessage)}
                  title={!soundEnabled ? 'Enable sound to hear the hint' : isSpeaking ? 'Speaking…' : 'Read aloud'}
                  disabled={isLoadingHint || !soundEnabled}
                >
                  {isSpeaking ? '🔈' : '🔊'}
                </button>
              </div>
              <p className="hint-text">
                {isLoadingHint ? '⏳ Loading hint…' : hintMessage}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* ── Camera Modal ── */}
      {showCamera && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#1A2340', margin: 0 }}>📷 Camera</h2>
              <button onClick={closeCamera} style={{ background: '#F3F4F6', border: 'none', borderRadius: '8px', width: '32px', height: '32px', fontSize: '16px', cursor: 'pointer', fontWeight: '700', color: '#4B5563' }}>✕</button>
            </div>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', borderRadius: '12px', background: '#000', maxHeight: '320px', objectFit: 'cover' }}
            />
            <button
              onClick={capturePhoto}
              style={{ padding: '12px 0', width: '100%', background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '800', cursor: 'pointer', fontFamily: 'Segoe UI, Inter, sans-serif' }}
            >
              📸 Capture
            </button>
          </div>
        </div>
      )}

      {/* ── Celebration Popup ── */}
      {showCelebration && (
        <div className="celebration-overlay">
          <div className="celebration-content">
            <div className="celebration-emoji">🎉</div>
            <h2 className="celebration-title">Excellent Work! 👏</h2>
            <p className="celebration-message">
              You correctly identified the emotion!<br />
              Keep practicing and you'll become an expert! ⭐
            </p>
            <div className="celebration-confetti">
              <span>🎊</span>
              <span>🌟</span>
              <span>💫</span>
              <span>⭐</span>
              <span>🎈</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmotionCornerStage2;
